import { useState, useEffect, useCallback, useRef } from 'react';

// One tab per calculation. The /mcc edit page autosaves every change straight to the user_quote (no Save
// button) and there is no server-side optimistic locking, so two tabs editing the SAME quote save over each
// other and interleave into a mixed state; they also share ONE client-side response cache (the IndexedDB store
// is keyed by user + fingerprint + request url, nothing per-tab), so the second tab confuses the first. Rather
// than reconcile two tabs, we let only one hold the quote at a time and send the second to a block screen.
//
// The gate is the Web Locks API: the owning tab holds an exclusive lock named for the quote for as long as its
// document lives (the callback returns a promise we keep pending), and a second tab that asks with
// `ifAvailable` is handed a null lock and knows another tab already has it. The lock is released the moment the
// owning tab's document goes away (close, reload, navigation) -- the browser does it, so there is no stale lock
// to clean up -- which is also why "Try again" recovers on its own once the other tab is gone.
//
// Same browser profile only, which is exactly the scope of the confusion: the shared IndexedDB cache lives in
// the profile. The same quote open on two DEVICES is a server-side lost-update problem this does not address.

const lockNameFor = (userQuoteId) => `frank-quote-${userQuoteId}`;

const webLocksSupported = () => (
  typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function'
);

// 'checking' -- still asking the browser whether another tab holds this quote (renders nothing, resolves in a
//              microtask, mirrors how the page already holds blank until its cache hydrates).
// 'owned'    -- this tab holds the lock, so it may edit (also the fail-open state when the browser has no Web
//              Locks: never trap a legitimate single-tab user out of their own quote).
// 'blocked'  -- another tab already holds this quote; show the block screen instead of the editor.
const useFrankQuoteTabLock = (userQuoteId) => {
  const [status, setStatus] = useState(() => (webLocksSupported() ? 'checking' : 'owned'));
  // Resolving this frees the lock this tab holds; null when this tab holds nothing.
  const releaseHeldLockRef = useRef(null);

  const acquire = useCallback(() => {
    // Free a lock a previous attempt is holding before re-requesting, so a retry can never leave two held.
    if (releaseHeldLockRef.current) {
      releaseHeldLockRef.current();
      releaseHeldLockRef.current = null;
    }
    if (!webLocksSupported() || userQuoteId == null) {
      setStatus('owned');
      return;
    }
    setStatus('checking');
    navigator.locks.request(lockNameFor(userQuoteId), { ifAvailable: true }, (lock) => {
      if (lock === null) {
        setStatus('blocked');
        return undefined; // Held nothing; resolving now leaves the real owner's lock untouched.
      }
      setStatus('owned');
      // Hold the lock for the life of this document by never resolving until we choose to release it.
      return new Promise((resolve) => { releaseHeldLockRef.current = resolve; });
    }).catch(() => {
      // A genuine Web Locks failure -- fail open rather than trap the user out of their quote.
      setStatus('owned');
    });
  }, [userQuoteId]);

  useEffect(() => {
    acquire();
    // The browser releases the lock when the document is destroyed, but resolving on pagehide releases it
    // deterministically so a same-tab reload or a forward/back navigation re-acquires cleanly.
    const releaseHeldLock = () => {
      if (releaseHeldLockRef.current) {
        releaseHeldLockRef.current();
        releaseHeldLockRef.current = null;
      }
    };
    window.addEventListener('pagehide', releaseHeldLock);
    return () => {
      window.removeEventListener('pagehide', releaseHeldLock);
      releaseHeldLock();
    };
  }, [acquire]);

  return { status, retry: acquire };
};

export default useFrankQuoteTabLock;
